import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import type { PropertyFileResponse, PropertyFileTreeNode } from '@/shared/api';
import { collectFolderNodes } from './akteBrowserNav';
import { categoryOf, isValidMoveTarget } from './akteFolderRules';
import { folderDisplayName, folderDisplayPathWithinCategory } from './akteFolderNames';
import { FolderTargetPicker } from './FolderTargetPicker';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: PropertyFileResponse;
  tree: PropertyFileTreeNode;
  onMove: (targetFolder: string) => void;
  onCreateSubfolder: () => void;
}

/** „Verschieben nach…" — Ziel-Ordner-Picker, nur Ordner derselben Top-Kategorie (§4.6). */
export function MoveFileDialog({
  open,
  onOpenChange,
  file,
  tree,
  onMove,
  onCreateSubfolder,
}: Props) {
  const [target, setTarget] = useState<string | null>(null);
  const category = categoryOf(file.folder);

  const targets = useMemo(
    () =>
      collectFolderNodes(tree)
        .filter((n) => isValidMoveTarget(file.folder, n.path))
        .map((n) => ({ path: n.path, label: folderDisplayPathWithinCategory(n.path) })),
    [tree, file.folder],
  );

  function confirm() {
    if (target) {
      onMove(target);
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Verschieben nach…</DialogTitle>
          <DialogDescription>
            Nur Ordner in{' '}
            <strong>{category ? folderDisplayName(category) : 'dieser Kategorie'}</strong> — die
            Kategorie bleibt gleich.
          </DialogDescription>
        </DialogHeader>
        <FolderTargetPicker
          targets={targets}
          selected={target}
          onSelect={setTarget}
          onCreateSubfolder={onCreateSubfolder}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button variant="brand" disabled={!target} onClick={confirm}>
            Verschieben
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
